import { sum } from 'lodash-es'

import type { MarkupSettings } from '../models/account-user.ts'
import type { Currency } from '../models/common.ts'
import type { ExchangeRateMap } from '../models/exchange-rate.ts'
import type { ApiChargeDTO, BaseQuoteModel, QuoteDTO } from '../models/quote.ts'
import { parseCurrency } from './currency.ts'
import { capitalizeFirstLetter } from './string-helpers.ts'

interface QuoteWithProviderInfo {
  providerCode?: string
  assetCarrierCode?: string
}

type QuoteProps = Pick<
  QuoteDTO,
  | 'amount'
  | 'amountUsd'
  | 'charges'
  | 'currency'
  | 'source'
  | 'status'
  | 'method'
  | 'hasMarkup'
>

type UsdMarkupResult = {
  chargeAmountUsd: number
  description: string
  method: MarkupCalculationMethod
}

const calculateFlatMarkupUsd = (amount: number): UsdMarkupResult => ({
  chargeAmountUsd: parseCurrency(amount),
  description: `$${amount} markup`,
  method: 'flat',
})

const calculatePercentageMarkupUsd = (
  quoteAmountUsd: number,
  percent: number,
  minimum: number | undefined,
  maximum: number | undefined,
): UsdMarkupResult => {
  const multiplier = (percent || 0) / 100
  const multiplied = quoteAmountUsd * multiplier
  const amountToApply = Math.min(
    Math.max(minimum || 0, multiplied),
    maximum || Infinity,
  )
  const minApplied = amountToApply === minimum
  const maxApplied = amountToApply === maximum
  const method = minApplied ? 'minimum' : maxApplied ? 'maximum' : 'percent'
  const chargeAmountUsd = parseCurrency(amountToApply)
  return {
    chargeAmountUsd,
    description:
      method === 'percent'
        ? `${percent}% markup`
        : `$${chargeAmountUsd} markup`,
    method,
  }
}

const generateMarkupCharge = (
  chargeAmount: number,
  currency: Currency,
  description: string,
): ApiChargeDTO => {
  return {
    amount: parseCurrency(chargeAmount),
    currency,
    code: 'FVMU',
    name: 'freightview markup',
    providerDetail: description,
  }
}

const resolveUsdRate = (
  quote: Pick<QuoteProps, 'amount' | 'amountUsd' | 'currency'>,
  exchangeRates: ExchangeRateMap | undefined,
): number => {
  const currency = quote.currency ?? 'usd'
  if (currency === 'usd') return 1
  const loadRate = exchangeRates?.[currency]
  if (loadRate) return loadRate
  if (quote.amountUsd) return quote.amount / quote.amountUsd
  return 1
}

const toUsdAmount = (
  quote: Pick<QuoteProps, 'amount' | 'amountUsd' | 'currency'>,
  rate: number,
): number => {
  if ((quote.currency ?? 'usd') === 'usd') return quote.amount
  if (quote.amountUsd != null) return quote.amountUsd
  return parseCurrency(quote.amount / rate)
}

export type MarkupCalculationMethod =
  | 'flat'
  | 'percent'
  | 'minimum'
  | 'maximum'
  | 'none'
export type MarkupCalculation = {
  amount: number
  charge: ApiChargeDTO | undefined
  method: MarkupCalculationMethod
}

const markupIsApplicable = (
  quote: QuoteProps,
  markup: MarkupSettings | undefined,
): boolean => {
  const quoteIsApplicable =
    (quote.source !== 'shipper' || quote.method === 'upload') &&
    quote.status !== 'error'
  return quoteIsApplicable && !!(markup?.amount || markup?.percent)
}

const calculateMarkup = (
  quote: QuoteProps,
  markup: MarkupSettings | undefined,
  exchangeRates?: ExchangeRateMap,
): MarkupCalculation => {
  if (!markupIsApplicable(quote, markup)) {
    return { amount: quote.amount, charge: null, method: 'none' }
  }

  const currency = quote.currency ?? 'usd'
  const rate = resolveUsdRate(quote, exchangeRates)
  const usdAmount = toUsdAmount(quote, rate)

  const usdResult = markup.percent
    ? calculatePercentageMarkupUsd(
        usdAmount,
        markup.percent,
        markup.minimum,
        markup.maximum,
      )
    : calculateFlatMarkupUsd(markup.amount)

  const chargeAmount = parseCurrency(usdResult.chargeAmountUsd * rate)
  const totalAmount = parseCurrency(quote.amount + chargeAmount)

  return {
    amount: totalAmount,
    charge: generateMarkupCharge(chargeAmount, currency, usdResult.description),
    method: usdResult.method,
  }
}

const applyMarkup = <T extends QuoteProps>(
  quote: T,
  markupSettings: MarkupSettings | undefined,
  exchangeRates?: ExchangeRateMap,
): T => {
  if (!quote) return quote

  if (!markupSettings || quote.hasMarkup) return quote

  const markupCalc = calculateMarkup(quote, markupSettings, exchangeRates)
  if (markupCalc.method === 'none') return quote

  return {
    ...quote,
    amount: markupCalc.amount,
    hasMarkup: true,
    charges: combineCharges(quote, markupCalc),
  }
}

const combineCharges = (
  quote: QuoteProps,
  markupCalc: MarkupCalculation,
): ApiChargeDTO[] => {
  const baseCharge: ApiChargeDTO = {
    name: 'linehaul',
    amount: quote.amount,
    currency: quote.currency ?? 'usd',
    code: 'LNH',
  }
  return [
    ...(quote.charges || []),
    ...(!quote.charges?.length && markupCalc.charge ? [baseCharge] : []),
    ...(markupCalc.charge ? [markupCalc.charge] : []),
  ]
}

const removeMarkup = <T extends Pick<BaseQuoteModel, 'amount' | 'charges'>>(
  quote: T,
): T => {
  const markupCharge = quote.charges?.find(c => c.name === 'freightview markup')
  const charges = quote.charges?.filter(c => c.name !== 'freightview markup')
  return {
    ...quote,
    amount: charges?.length
      ? sum(charges.map(c => c.amount))
      : quote.amount - (markupCharge?.amount || 0),
    charges,
  }
}

/**
 * Determines if a quote is brokered by comparing the provider code with the asset carrier code.
 * A quote is considered brokered when the provider (who quoted the rate) is different from
 * the asset carrier (who will physically handle the shipment).
 *
 * @param quote - Quote object with provider and asset carrier codes
 * @returns true if the quote is brokered, false otherwise
 */
function isBrokerQuote(quote?: QuoteWithProviderInfo): boolean {
  if (!quote?.providerCode || !quote?.assetCarrierCode) {
    return false
  }

  return quote.providerCode !== quote.assetCarrierCode
}

type ServiceTypeLabelQuote = Pick<
  BaseQuoteModel,
  'serviceId' | 'serviceDescription'
>

function getKnownServiceTypeLabel(serviceId: string): string | undefined {
  if (/guarantee/i.test(serviceId)) return 'Guaranteed'
  if (/standard/i.test(serviceId)) return 'Standard'
  if (/volume/i.test(serviceId)) return 'Spot Volume'
  return undefined
}

function resolveServiceTypeLabel(
  quote: ServiceTypeLabelQuote,
  capitalizeUnknown = false,
): string {
  if (quote.serviceDescription) {
    return quote.serviceDescription
  }

  const knownLabel = getKnownServiceTypeLabel(quote.serviceId)
  if (knownLabel) {
    return knownLabel
  }

  return capitalizeUnknown
    ? capitalizeFirstLetter(quote.serviceId)
    : quote.serviceId
}

const describeMarkupSettings = (
  settings: MarkupSettings | undefined,
): string | null => {
  if (!settings) return null
  const isPercent = !!settings.percent
  const num = isPercent ? settings.percent : settings.amount
  if (!num) return null
  const label = isPercent ? `${num}%` : `$${num}`
  return [label, 'markup', getMinMaxString(settings)].filter(Boolean).join(' ')
}

const getMinMaxString = (settings: MarkupSettings | undefined): string => {
  if (!settings) return ''
  const { minimum, maximum } = settings
  const minimumString = minimum ? `min:$${minimum}` : ''
  const maximumString = maximum ? `max:$${maximum}` : ''
  return [minimumString, maximumString].filter(Boolean).join('/')
}

export const quoteUtils = {
  applyMarkup,
  removeMarkup,
  calculateMarkup,
  describeMarkupSettings,
  isBrokerQuote,
  resolveServiceTypeLabel,
}
